<template>
  <div class="relative inline-block group">
    <!-- SVG Progress Ring -->
    <svg
      :class="sizeClasses"
      class="relative z-10 transform -rotate-90"
      :viewBox="`0 0 ${svgSize} ${svgSize}`"
      style="overflow: visible;"
    >
      <!-- Background Circle -->
      <circle
        :cx="svgSize / 2"
        :cy="svgSize / 2"
        :r="radius"
        :stroke="bgColor"
        :stroke-width="strokeWidth"
        fill="none"
      />
      
      <!-- Progress Circle -->
      <circle
        :cx="svgSize / 2"
        :cy="svgSize / 2"
        :r="radius"
        :stroke="dynamicProgressColor"
        :stroke-width="strokeWidth"
        fill="none"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="strokeDashoffset"
        stroke-linecap="round"
        class="transition-all duration-300 ease-out progress-circle"
        ref="progressRef"
      />
    </svg>
    
    <!-- Avatar Container -->
    <div 
      class="absolute inset-0 flex items-center justify-center"
      :style="{ padding: `${strokeWidth + 2}px` }"
    >
      <div class="rounded-full overflow-hidden w-full h-full ring-2 ring-white shadow-md">
        <img
          :src="avatar || defaultAvatar"
          :alt="`Avatar ${name || ''}`"
          class="w-full h-full object-cover block"
          @error="handleImageError"
          loading="lazy"
        />
      </div>
    </div>
    
    <!-- Percentage Badge -->
    <div
      v-if="showPercentage"
      class="absolute -bottom-2 -right-2 bg-white rounded-full flex items-center justify-center border-2 shadow-lg transform scale-0 group-hover:scale-100 transition-transform duration-300 z-20"
      :class="percentageClasses"
      :style="{ 
        color: dynamicProgressColor,
        borderColor: dynamicProgressColor 
      }"
    >
      <span class="text-xs font-bold leading-none">{{ Math.round(finalProgress) }}%</span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'

const props = defineProps({
  progress: {
    type: Number,
    default: 0,
    validator: (value) => value >= 0 && value <= 100
  },
  avatar: {
    type: String,
    default: null
  },
  name: {
    type: String,
    default: ''
  },
  showPercentage: {
    type: Boolean,
    default: false
  },
  progressColor: {
    type: String,
    default: '#10b981'
  },
  bgColor: {
    type: String,
    default: '#e5e7eb'
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
  },
  animate: {
    type: Boolean,
    default: true
  },
  animationDuration: {
    type: Number,
    default: 1000 // milliseconds
  },
  easing: {
    type: String,
    default: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' // easeOutQuart
  },
  initialDelay: {
    type: Number,
    default: 500 // milliseconds before initial animation starts
  }
})

const emit = defineEmits(['progress-change', 'update:progress', 'animation-complete'])

const progressRef = ref(null)
const internalProgress = ref(0) // Start at 0 for animation
const finalProgress = ref(props.progress)
const isAnimating = ref(false)
const defaultAvatar = computed(() => 
  'https://ui-avatars.com/api/?name=' + encodeURIComponent(props.name || 'User') + '&size=128&background=6B7280&color=fff'
)

// Computed properties
const clampedProgress = computed(() => Math.min(Math.max(internalProgress.value, 0), 100))

// SVG-specific computed properties
const svgSize = computed(() => {
  const sizes = {
    sm: 48,
    md: 80,
    lg: 96,
    xl: 128
  }
  return sizes[props.size] || sizes.md
})

const strokeWidth = computed(() => {
  const widths = {
    sm: 3,
    md: 4,
    lg: 5,
    xl: 6
  }
  return widths[props.size] || widths.md
})

// Adjust radius to account for stroke width
const radius = computed(() => (svgSize.value - strokeWidth.value) / 2)
const circumference = computed(() => 2 * Math.PI * radius.value)
const strokeDashoffset = computed(() => {
  const offset = circumference.value - (clampedProgress.value / 100) * circumference.value
  return offset
})

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'w-12 h-12',
    md: 'w-20 h-20',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32'
  }
  return sizes[props.size] || sizes.md
})

const percentageClasses = computed(() => {
  const sizes = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-7 h-7',
    xl: 'w-8 h-8'
  }
  return sizes[props.size] || sizes.md
})

const dynamicProgressColor = computed(() => {
  const progress = Math.round(finalProgress.value)
  if (progress === 0) return props.bgColor
  if (progress < 30) return '#ef4444' // Red
  if (progress < 70) return '#f59e0b' // Amber
  return props.progressColor // Green
})

// Easing functions - define before use
const easeInOutCubic = (t) => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

// Animation Methods
const animateToProgress = async (targetProgress) => {
  if (!props.animate || !progressRef.value) {
    internalProgress.value = targetProgress
    return
  }

  isAnimating.value = true
  
  // Reset to 0 first (instantly)
  internalProgress.value = 0
  await nextTick()
  
  // Trigger reflow to restart CSS transition
  progressRef.value.offsetHeight
  
  // Animate from 0 to target
  const startTime = performance.now()
  const duration = props.animationDuration
  const startProgress = 0
  const endProgress = targetProgress

  const animate = (currentTime) => {
    const elapsed = Math.min(currentTime - startTime, duration)
    const easeProgress = easeInOutCubic(elapsed / duration)
    const progress = startProgress + (endProgress - startProgress) * easeProgress
    
    internalProgress.value = progress
    
    // Emit progress updates during animation
    emit('progress-change', progress)
    
    if (elapsed < duration) {
      requestAnimationFrame(animate)
    } else {
      // Ensure we hit exactly the target
      internalProgress.value = targetProgress
      emit('progress-change', targetProgress)
      emit('update:progress', targetProgress)
      emit('animation-complete', targetProgress)
      
      // Add completion effect when reaching 100%
      if (targetProgress >= 100) {
        // Wait a moment for the main animation to complete
        setTimeout(() => {
          if (progressRef.value) {
            // Force a reflow to ensure animation restarts
            progressRef.value.style.animation = 'none'
            progressRef.value.offsetHeight // Trigger reflow
            progressRef.value.style.animation = 'completion-pulse 0.6s ease-in-out'
            
            // Clean up after animation
            setTimeout(() => {
              if (progressRef.value) {
                progressRef.value.style.animation = ''
              }
            }, 600)
          }
        }, 50)
      }
      
      isAnimating.value = false
    }
  }
  
  requestAnimationFrame(animate)
}

// Methods
const handleImageError = (event) => {
  event.target.src = defaultAvatar.value
}

// Watch for progress changes - MUST be after all function definitions
watch(() => props.progress, async (newVal) => {
  const targetProgress = Math.min(Math.max(newVal, 0), 100)
  finalProgress.value = targetProgress
  
  if (props.animate) {
    await animateToProgress(targetProgress)
  } else {
    internalProgress.value = targetProgress
    emit('progress-change', targetProgress)
    emit('update:progress', targetProgress)
  }
})

// Initial animation on mount with delay
onMounted(() => {
  // Start at 0
  internalProgress.value = 0
  finalProgress.value = 0
  
  // Delay the initial animation so user can see it
  setTimeout(() => {
    const targetProgress = Math.min(Math.max(props.progress, 0), 100)
    finalProgress.value = targetProgress
    
    if (props.animate) {
      animateToProgress(targetProgress)
    } else {
      internalProgress.value = targetProgress
      emit('progress-change', targetProgress)
      emit('update:progress', targetProgress)
    }
  }, props.initialDelay)
})
</script>

<style scoped>
/* SVG circle animation */
.animate-draw circle:last-child {
  transition: stroke-dashoffset 1.5s ease-in-out;
}

/* Completion animation - scale pulse */
@keyframes completion-pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.progress-circle {
  transform-origin: center;
}

.animate-completion {
  animation: completion-pulse 0.6s ease-in-out;
  transform-origin: center;
}

/* Hover effects */
.group:hover svg {
  transform: scale(1.03) rotate(-90deg);
  transition: transform 0.3s ease-out;
}

/* Completion sparkle effect */
@keyframes sparkle {
  0% {
    filter: drop-shadow(0 0 0 transparent);
  }
  50% {
    filter: drop-shadow(0 0 10px currentColor);
  }
  100% {
    filter: drop-shadow(0 0 0 transparent);
  }
}

.completion-sparkle {
  animation: sparkle 0.6s ease-out;
}

/* Percentage badge pulse */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}

.group:hover .animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  svg circle {
    stroke-width: 2;
  }
}
</style>